import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [groups, setGroups] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    city: '',
    state: '',
    region: '',
    group_link: '',
    group_name: '',
    member_count: 0,
    moderator: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp-groups`);
      setGroups(response.data);
    } catch (e) {
      toast.error('Failed to fetch groups');
      console.error(e);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/whatsapp-groups`, formData);
      toast.success('Group added successfully!');
      setShowAddModal(false);
      resetForm();
      fetchGroups();
    } catch (e) {
      toast.error('Failed to add group');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/admin/whatsapp-groups/${editingGroup.id}`, formData);
      toast.success('Group updated successfully!');
      setEditingGroup(null);
      resetForm();
      fetchGroups();
    } catch (e) {
      toast.error('Failed to update group');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      await axios.delete(`${API}/admin/whatsapp-groups/${groupId}`);
      toast.success('Group deleted successfully!');
      fetchGroups();
    } catch (e) {
      toast.error('Failed to delete group');
      console.error(e);
    }
  };

  const startEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      city: group.city,
      state: group.state,
      region: group.region,
      group_link: group.group_link,
      group_name: group.group_name,
      member_count: group.member_count,
      moderator: group.moderator || ''
    });
  };

  const resetForm = () => {
    setFormData({
      city: '',
      state: '',
      region: '',
      group_link: '',
      group_name: '',
      member_count: 0,
      moderator: ''
    });
  };

  const cancelEdit = () => {
    setEditingGroup(null);
    resetForm();
  };

  return (
    <div className="admin-page">
      {/* Header */}
      <section className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <div>
              <h1 className="admin-title">WhatsApp Groups Admin Panel</h1>
              <p className="admin-subtitle">Manage city-based WhatsApp groups</p>
            </div>
            <Button 
              className="btn-primary" 
              onClick={() => setShowAddModal(true)}
              data-testid="add-group-btn"
            >
              <Plus size={20} />
              Add New Group
            </Button>
          </div>
        </div>
      </section>

      {/* Groups Table */}
      <section className="admin-content">
        <div className="container">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>City</th>
                  <th>State</th>
                  <th>Region</th>
                  <th>Group Name</th>
                  <th>Members</th>
                  <th>Moderator</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-state">
                      No groups added yet. Click "Add New Group" to get started.
                    </td>
                  </tr>
                ) : (
                  groups.map((group, index) => (
                    <tr key={group.id || index}>
                      <td>{group.city}</td>
                      <td>{group.state}</td>
                      <td>{group.region}</td>
                      <td>{group.group_name}</td>
                      <td>{group.member_count}</td>
                      <td>{group.moderator || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => startEdit(group)}
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(group.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Add Group Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="admin-modal">
          <DialogHeader>
            <DialogTitle>Add New WhatsApp Group</DialogTitle>
            <DialogDescription>
              Fill in the details to add a new city-based WhatsApp group
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="e.g., Mumbai"
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  placeholder="e.g., Maharashtra"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Region *</label>
              <select
                className="form-select"
                value={formData.region}
                onChange={(e) => setFormData({...formData, region: e.target.value})}
                required
              >
                <option value="">Select Region</option>
                <option value="North">North</option>
                <option value="South">South</option>
                <option value="East">East</option>
                <option value="West">West</option>
                <option value="Central">Central</option>
                <option value="Northeast">Northeast</option>
              </select>
            </div>
            <div className="form-group">
              <label>Group Name *</label>
              <Input
                value={formData.group_name}
                onChange={(e) => setFormData({...formData, group_name: e.target.value})}
                placeholder="e.g., Mumbai Seniors Group"
                required
              />
            </div>
            <div className="form-group">
              <label>WhatsApp Group Link *</label>
              <Input
                value={formData.group_link}
                onChange={(e) => setFormData({...formData, group_link: e.target.value})}
                placeholder="https://chat.whatsapp.com/..."
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Member Count</label>
                <Input
                  type="number"
                  value={formData.member_count}
                  onChange={(e) => setFormData({...formData, member_count: parseInt(e.target.value) || 0})}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label>Moderator</label>
                <Input
                  value={formData.moderator}
                  onChange={(e) => setFormData({...formData, moderator: e.target.value})}
                  placeholder="Moderator name"
                />
              </div>
            </div>
            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={loading}>
                <Save size={18} />
                {loading ? 'Adding...' : 'Add Group'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Group Modal */}
      <Dialog open={!!editingGroup} onOpenChange={(open) => !open && cancelEdit()}>
        <DialogContent className="admin-modal">
          <DialogHeader>
            <DialogTitle>Edit WhatsApp Group</DialogTitle>
            <DialogDescription>
              Update the group details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Region *</label>
              <select
                className="form-select"
                value={formData.region}
                onChange={(e) => setFormData({...formData, region: e.target.value})}
                required
              >
                <option value="">Select Region</option>
                <option value="North">North</option>
                <option value="South">South</option>
                <option value="East">East</option>
                <option value="West">West</option>
                <option value="Central">Central</option>
                <option value="Northeast">Northeast</option>
              </select>
            </div>
            <div className="form-group">
              <label>Group Name *</label>
              <Input
                value={formData.group_name}
                onChange={(e) => setFormData({...formData, group_name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>WhatsApp Group Link *</label>
              <Input
                value={formData.group_link}
                onChange={(e) => setFormData({...formData, group_link: e.target.value})}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Member Count</label>
                <Input
                  type="number"
                  value={formData.member_count}
                  onChange={(e) => setFormData({...formData, member_count: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="form-group">
                <label>Moderator</label>
                <Input
                  value={formData.moderator}
                  onChange={(e) => setFormData({...formData, moderator: e.target.value})}
                />
              </div>
            </div>
            <div className="form-actions">
              <Button type="button" variant="outline" onClick={cancelEdit}>
                <X size={18} />
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={loading}>
                <Save size={18} />
                {loading ? 'Updating...' : 'Update Group'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
